import { Product, Variant } from "@~/types/types";
import { removeEmpty } from "@~/utils/removeEmpty.ts";
import config from "@~/envConfig";

const BASKET_COOKIE = /(?:^|; ?)cached_basket=([^;]+)/;

type ProductInBasket = {
  pimProductId: string;
  skuId: string;
  quantity: number;
};

export type ProductsInBasket = ProductInBasket[] | null;

export function getProductsFromCachedBasketCookie(): ProductsInBasket {
  const [, cachedBasketCookie] = document?.cookie.match(BASKET_COOKIE) || [];

  if (cachedBasketCookie) {
    const basket = cachedBasketCookie.split(/\|?\^/);

    if (basket?.length > 2) {
      const basketWithoutTotals = basket.slice(0, basket.length - 2);

      const products = basketWithoutTotals.map((product) => {
        // Item Type | product id | item id | sku id | price | quantity | ^ repeat ^ subTotal= ^ totalNumberOfItems=
        const [, pimProductId, , skuId, , quantity] = product.split("|");
        return pimProductId
          ? {
              pimProductId,
              quantity: Number(quantity || 0),
              skuId: String(skuId),
            }
          : null;
      });

      if (products.length) {
        return removeEmpty(products);
      }
    }
  }

  return null;
}

const hasAvailableToOrderFromMessage = (product: Product): boolean =>
  !!product?.availability?.futureAvailability?.availableToOrderFromMessage;

const hasBackInStockEventAvailable = (product: Product): boolean => {
  return product?.variants?.some(
    (v: Variant) => v.customerNotifiableEvents?.backInStock,
  );
};

const hasNewlyInStockEventAvailable = (product: Product): boolean => {
  return product?.variants?.some(
    (v: Variant) => v.customerNotifiableEvents?.newlyInStock,
  );
};

const hasNotifiableEvents = (product: Product): boolean =>
  hasBackInStockEventAvailable(product) ||
  hasOnReleaseEventAvailable(product) ||
  hasPreOrderLaunchEventAvailable(product) ||
  hasNewlyInStockEventAvailable(product);

const hasOnReleaseEventAvailable = (product: Product): boolean => {
  return product?.variants?.some(
    (v: Variant) => v.customerNotifiableEvents?.onRelease,
  );
};

const hasPreOrderLaunchEventAvailable = (product: Product): boolean => {
  return product?.variants?.some(
    (v: Variant) => v.customerNotifiableEvents?.onPreorderLaunch,
  );
};

const hasValidPrice = (
  variantPriceRange: Product["variantPriceRange"],
): boolean => {
  if (!variantPriceRange) return false;
  return (
    parseFloat(variantPriceRange?.value?.max) > 0 &&
    parseFloat(variantPriceRange?.value?.min) > 0
  );
};

export const isAddToBasketProduct = (product: Product): boolean => {
  const { variantPriceRange } = product;

  if (
    !hasValidPrice(variantPriceRange) ||
    isMeteredProduct(product) ||
    isFutureReleaseProduct(product) ||
    isInStoreOnlyProduct(product) ||
    isM2MProduct(product) ||
    (!isMultiSkuProduct(product) && isShipFromStoreProduct(product)) ||
    isOutletProduct(product) ||
    isOutOfStock(product) ||
    isUnsupportedProductType(product)
  ) {
    return false;
  }
  return true;
};

const isAnyShapeAnyFabricProduct = (product: Product): boolean => {
  return (
    product?.configurationRequirements?.type.some(
      (v) => v === "AnyShapeAnyFabricFurniture",
    ) || false
  );
};

export const isBladedArticleProduct = (product: Product): boolean => {
  return product?.variants?.some(
    (v: Variant) => v.sellingRestrictions?.age?.bladedArticle,
  );
};

export const isEligibleForQuickView = (product: Product): boolean => {
  const isPreorderBackInStock =
    isOutOfStock(product) && hasBackInStockEventAvailable(product);

  const isFutureRelease =
    isPreorderBackInStock ||
    hasNewlyInStockEventAvailable(product) ||
    hasPreOrderLaunchEventAvailable(product) ||
    hasOnReleaseEventAvailable(product);

  if (
    !hasValidPrice(product.variantPriceRange) ||
    isMeteredProduct(product) ||
    isInStoreOnlyProduct(product) ||
    isM2MProduct(product) ||
    isOutletProduct(product) ||
    (isOutOfStock(product) && !isFutureRelease) ||
    isUnsupportedProductType(product)
  ) {
    return false;
  }

  return true;
};

export const isEmailWhenBackInStockProduct = (product: Product): boolean => {
  if (isInStoreOnlyProduct(product)) return false;
  if (
    (isOutOfStock(product) && hasBackInStockEventAvailable(product)) ||
    hasNewlyInStockEventAvailable(product) ||
    hasPreOrderLaunchEventAvailable(product) ||
    hasOnReleaseEventAvailable(product)
  ) {
    return true;
  }

  return false;
};

export const isEmailWhenBackInStockVariant = (variant: Variant): boolean => {
  if (variant?.availability?.unavailableReason === "inStoreOnly") return false;
  if (
    (variant?.availability?.unavailableReason === "outOfStock" &&
      variant.customerNotifiableEvents?.backInStock) ||
    variant.customerNotifiableEvents?.newlyInStock ||
    variant.customerNotifiableEvents?.onPreorderLaunch ||
    variant.customerNotifiableEvents?.onRelease
  ) {
    return true;
  }
  return false;
};

export const isFutureReleaseProduct = (product: Product): boolean =>
  hasAvailableToOrderFromMessage(product) ||
  (hasBackInStockEventAvailable(product) && isOutOfStock(product)) || //preorder is back in stock
  hasNewlyInStockEventAvailable(product) ||
  hasPreOrderLaunchEventAvailable(product) ||
  hasOnReleaseEventAvailable(product);

export const isInStoreOnlyProduct = (product: Product): boolean => {
  if (product?.variants?.length === 0) return false;
  const inStoreOnly =
    product?.variants?.every(
      (v: Variant) => v?.availability?.unavailableReason === "inStoreOnly",
    ) || false;

  return inStoreOnly;
};

export const isM2MProduct = (product: Product): boolean => {
  return (
    product?.configurationRequirements?.type.some(
      (v) =>
        v === "MadeToMeasureCurtain" ||
        v == "MadeToMeasureRomanBlind" ||
        v == "MadeToMeasureRollerBlind",
    ) || false
  );
};

export const isM2MRomanBlindOrCurtain = (product: Product): boolean => {
  return (
    product?.configurationRequirements?.type.some(
      (v) => v === "MadeToMeasureCurtain" || v == "MadeToMeasureRomanBlind",
    ) || false
  );
};

export const isMeteredProduct = (product: Product): boolean =>
  product?.unitOfMeasure === "METRE";

export const isMultiSkuProduct = (product: Product): boolean =>
  product?.variants?.length > 1 || product?.isMultiSku === true;

export const isOutletProduct = (product: Product): boolean =>
  product?.variants?.some((v: Variant) => v.returnsOutlet === "true");

export const isOutOfStock = (product: Product): boolean => {
  if (product?.variants?.length === 0) return false;
  const outOfStock =
    product?.variants?.every(
      (v: Variant) => v.availability?.unavailableReason === "outOfStock",
    ) || false;
  return outOfStock;
};

export const isPermanentlyOutOfStockProduct = (product: Product): boolean => {
  if (
    hasNotifiableEvents(product) ||
    isInStoreOnlyProduct(product) ||
    isPreOrderable(product) ||
    hasBackInStockEventAvailable(product)
  )
    return false;

  if (isOutOfStock(product)) {
    return true;
  }

  return false;
};

const isPreOrderable = (product: Product): boolean =>
  hasAvailableToOrderFromMessage(product) ||
  !!product?.availability?.preOrder?.streetDateMessage;

export const isPreOrderWithoutNotifiableEventsProduct = (
  product: Product,
): boolean => {
  return isPreOrderable(product) && !hasNotifiableEvents(product);
};

export const isProductInCachedBasket = (
  pimProductId: string,
  cachedBasketData: ProductsInBasket,
): boolean => {
  if (!pimProductId || !cachedBasketData) return false;
  return cachedBasketData?.some(
    (product) => product.pimProductId === pimProductId,
  );
};

export const isProductSetProduct = (product: Product): boolean =>
  product.isProductSet === true;

export const isShipFromStoreProduct = (product: Product): boolean =>
  product?.variants?.some((v: Variant) => v.availability?.shipFromStore);

export const isUnsupportedProductType = (product: Product): boolean => {
  return (
    isAnyShapeAnyFabricProduct(product) ||
    isProductSetProduct(product) ||
    !!product.isCompositeBundle
  );
};

export const isViewOptionsProduct = (product: Product): boolean => {
  return (
    isMeteredProduct(product) ||
    isM2MProduct(product) ||
    isMultiSkuProduct(product) ||
    (isOutOfStock(product) && !isEmailWhenBackInStockProduct(product))
  );
};

export const getProductUrl = (
  product: Product,
  variant: Variant | null,
): string | undefined => {
  const isProductSet = isProductSetProduct(product);

  const basePath = config.hostUrl;
  const pdpURL = `${basePath}${variant?.pdpURL.url}`;

  if (isProductSet && product.pdpURL.url) {
    return `${basePath}/browse${product.pdpURL.url}`;
  }
  const { associatedAdvertId } = product.advertising || {};
  const isKevelProduct = product.advertising?.clickUrl !== undefined;

  if (isKevelProduct) {
    return `${pdpURL}#kevelAdId=${associatedAdvertId}`;
  }

  return associatedAdvertId
    ? `${pdpURL}#citrusAdId=${associatedAdvertId}`
    : pdpURL;
};
